import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle, Banknote, FileSpreadsheet, Loader2, Stamp,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { AskDonorButton } from "@/components/common/AskDonorButton";
import { ClaimActions } from "@/components/common/ClaimActions";
import { DateField, isoDay } from "@/components/common/DateField";
import { rowClick, useRowDetail } from "@/components/common/DetailSheet";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime, shortDate } from "@/lib/format";

const startOfYear = () => isoDay(new Date(new Date().getFullYear(), 0, 1));

const STATUS_TONE = {
  claimed: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  submitted: "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300",
  acknowledged: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  part_repaid: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  repaid: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  reopened: "border-border bg-muted text-muted-foreground",
};

export default function Claims() {
  const { can, user } = useAuth();
  const [regime, setRegime] = useState("GB");
  const [range, setRange] = useState({ from: startOfYear(), to: isoDay(new Date()) });
  const [data, setData] = useState(null);
  const [claims, setClaims] = useState(null);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState("");
  const [fill, setFill] = useState({});
  const [fillOpen, setFillOpen] = useState("");
  const detail = useRowDetail();

  const load = useCallback(async () => {
    const [eligible, register] = await Promise.all([
      api.get("/claims/eligible", { params: { regime, date_from: range.from, date_to: range.to } }),
      api.get("/claims"),
    ]);
    setData(eligible.data);
    setClaims(register.data);
    setPicked([]);
  }, [regime, range.from, range.to]);

  useEffect(() => { load().catch(() => setData(false)); }, [load]);

  const toggle = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const build = async () => {
    setBusy("build");
    try {
      const res = await api.post("/claims", {
        regime, period_from: range.from, period_to: range.to, donation_ids: picked,
      });
      toast.success(`${res.data.claim_ref} built — ${res.data.gift_count} gifts locked`);
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : apiError(e));
    }
    setBusy("");
  };

  const saveDetails = async (row) => {
    setBusy(row.donation_id);
    try {
      await api.put(`/claims/donors/${row.donor_id}`, fill);
      toast.success(`${row.donor_name} updated`);
      setFillOpen("");
      setFill({});
      await load();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const download = async (claim) => {
    setBusy(claim.claim_ref);
    try {
      const res = await api.get(`/claims/${claim.claim_ref}/file.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${claim.claim_ref}-schedule.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`${claim.claim_ref} schedule downloaded`);
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  const printSummary = (claim) => {
    const rows = claim.rows.map((r) => `<tr><td>${r.reference}</td><td>${r.donor_name}</td>`
      + `<td>${r.donation_date || ""}</td><td style="text-align:right">${r.amount.toFixed(2)}</td>`
      + `<td>${r.receipt_no || ""}</td></tr>`).join("");
    const win = window.open("", "_blank");
    win.document.write(`<html><head><title>${claim.claim_ref}</title>
      <style>body{font-family:Helvetica,Arial;padding:40px;color:#111}
      h1{font-size:19px;margin:0 0 4px}p{font-size:12px;color:#555}
      table{border-collapse:collapse;width:100%;font-size:12px;margin-top:16px}
      td,th{border:1px solid #ddd;padding:6px 8px;text-align:left}
      tfoot td{font-weight:700}</style></head><body>
      <h1>${claim.claim_ref} — ${claim.regime_label}</h1>
      <p>${claim.form} · ${claim.authority}<br/>
      Period ${claim.period_from} to ${claim.period_to} · ${claim.gift_count} gifts ·
      total ${claim.currency} ${claim.total.toFixed(2)}
      ${claim.reclaim ? ` · reclaim ${claim.currency} ${claim.reclaim.toFixed(2)}` : ""}<br/>
      Built by ${claim.created_by} on ${new Date(claim.created_at).toLocaleString()}</p>
      <table><thead><tr><th>Gift</th><th>Donor</th><th>Date</th><th>Amount</th><th>Receipt</th></tr>
      </thead><tbody>${rows}</tbody>
      <tfoot><tr><td colspan="3">Total</td>
      <td style="text-align:right">${claim.total.toFixed(2)}</td><td></td></tr></tfoot></table>
      <p style="margin-top:28px;font-size:10px;color:#888">
      Generated from ServeCOR. Every gift in this claim is locked and cannot appear in another
      claim unless the President / Chairperson re-opens it.</p></body></html>`);
    win.document.close();
    win.print();
  };

  if (!data) return <Loading label="Reading the declarations we hold" />;
  const mayBuild = can("claim", "write");
  const mayReopen = user?.role === "board_chair" || user?.role === "platform_admin";

  return (
    <div className="space-y-6" data-testid="claims-page">
      <PageHeader
        eyebrow="Tax relief · Gift Aid & 80G"
        title="Turn the declarations you hold into a claim the authority accepts"
        description="Only gifts with a declaration, a posted status and every detail the authority insists on can be claimed. Building a claim locks those gifts so the same money can never be claimed twice."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={regime} onValueChange={setRegime}>
              <SelectTrigger data-testid="claim-regime-select"
                             className="h-9 w-[230px] bg-muted/40 text-xs">
                <SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="GB">United Kingdom · Gift Aid</SelectItem>
                <SelectItem value="IN">India · Section 80G</SelectItem>
              </SelectContent>
            </Select>
            <DateField value={range.from} testId="claim-from-input" placeholder="Period from"
                       clearable={false} disabled={{ after: new Date() }}
                       onChange={(v) => setRange({ ...range, from: v })}
                       className="h-9 w-[150px] text-[11.5px]" />
            <DateField value={range.to} testId="claim-to-input" placeholder="Period to"
                       clearable={false} disabled={{ after: new Date() }}
                       onChange={(v) => setRange({ ...range, to: v })}
                       className="h-9 w-[150px] text-[11.5px]" />
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Kpi testId="kpi-claim-ready" label="Gifts ready to claim" accent="emerald"
             value={data.counters.claimable} icon={Stamp} />
        <Kpi testId="kpi-claim-value" label={`Claimable value (${range.from} → ${range.to})`}
             accent="sky" value={data.counters.total.toLocaleString()} />
        <Kpi testId="kpi-claim-reclaim" label="Estimated reclaim" accent="violet"
             value={data.counters.reclaim ? data.counters.reclaim.toLocaleString() : "—"} />
        <Kpi testId="kpi-claim-incomplete" label="Cannot claim yet" accent="amber"
             value={data.counters.incomplete} icon={AlertTriangle} />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi testId="kpi-claim-in-flight" label="With the authority" accent="violet"
             value={claims?.counters?.submitted ?? 0} icon={Stamp} />
        <Kpi testId="kpi-claim-repaid" label="Repaid to date" accent="emerald"
             value={(claims?.counters?.repaid_value ?? 0).toLocaleString()} icon={Banknote} />
        <Kpi testId="kpi-claim-awaiting-posting" label="Postings awaiting approval" accent="amber"
             value={claims?.counters?.awaiting_posting ?? 0} />
      </div>

      <div className="rounded-xl border border-border bg-muted/20 p-3 text-[11.5px] text-muted-foreground"
           data-testid="claim-regime-note">
        <span className="text-foreground">{data.regime_label}</span> · {data.form} ·{" "}
        {data.authority}. {data.rate_note} Needed on every row:{" "}
        {data.required.map((r) => r.label).join(", ")}.
      </div>

      <Tabs defaultValue="build">
        <TabsList className="bg-muted/40">
          <TabsTrigger value="build" data-testid="tab-claim-build">Build a claim</TabsTrigger>
          <TabsTrigger value="incomplete" data-testid="tab-claim-incomplete">
            Cannot claim yet ({data.counters.incomplete})
          </TabsTrigger>
          <TabsTrigger value="register" data-testid="tab-claim-register">Claim register</TabsTrigger>
        </TabsList>

        <TabsContent value="build" className="mt-4">
          <Panel testId="panel-claim-build" kicker="Declarations held"
                 title="Gifts you can claim on"
                 actions={mayBuild && (
                   <div className="flex items-center gap-2">
                     <Button size="sm" variant="outline" data-testid="claim-select-all"
                             onClick={() => setPicked(picked.length === data.claimable.length
                               ? [] : data.claimable.map((r) => r.donation_id))}
                             className="h-8 rounded-full border-border text-xs">
                       {picked.length === data.claimable.length && picked.length
                         ? "Clear" : "Select all"}
                     </Button>
                     <Button size="sm" onClick={build} disabled={busy === "build" || !picked.length}
                             data-testid="claim-build-button"
                             className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                       {busy === "build" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                         : <FileSpreadsheet className="mr-1 h-3.5 w-3.5" />}
                       Build claim ({picked.length})
                     </Button>
                   </div>
                 )}>
            <Table testId="claim-eligible-table"
                   head={<><Th /><Th>Gift</Th><Th>Donor</Th><Th>Date</Th><Th>Amount</Th>
                     <Th>Receipt</Th><Th>Details held</Th></>}>
              {data.claimable.length === 0 && (
                <Empty colSpan={7} label="No gifts with a usable declaration in this window" />
              )}
              {data.claimable.map((r) => (
                <tr key={r.donation_id} className="row-hover cursor-pointer"
                    data-testid={`claim-row-${r.reference}`}
                    onClick={rowClick(() => detail.show(r, r.reference, r.reference, r.donor_name))}>
                  <Td>
                    <Checkbox checked={picked.includes(r.donation_id)} disabled={!mayBuild}
                              data-testid={`claim-pick-${r.reference}`}
                              onCheckedChange={() => toggle(r.donation_id)} />
                  </Td>
                  <Td className="font-mono text-[11px]">{r.reference}</Td>
                  <Td>
                    <div className="text-foreground">{r.donor_name}</div>
                    <div className="text-[10.5px] text-muted-foreground">{r.donor_code}</div>
                  </Td>
                  <Td className="font-mono text-[11px]">{shortDate(r.donation_date)}</Td>
                  <Td className="font-mono">{r.currency} {r.amount.toLocaleString()}</Td>
                  <Td className="font-mono text-[10.5px] text-muted-foreground">
                    {r.receipt_no || "—"}
                  </Td>
                  <Td>
                    <Chip className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
                      complete
                    </Chip>
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="incomplete" className="mt-4">
          <Panel testId="panel-claim-incomplete" kicker="Chase or complete"
                 title="Gifts held back because the authority needs more">
            <Table testId="claim-incomplete-table"
                   head={<><Th>Gift</Th><Th>Donor</Th><Th>Amount</Th><Th>Still needed</Th><Th /></>}>
              {data.incomplete.length === 0 && (
                <Empty colSpan={5} label="Nothing is being held back" />
              )}
              {data.incomplete.map((r) => (
                <tr key={r.donation_id} data-testid={`claim-incomplete-${r.reference}`}
                    className="row-hover">
                  <Td className="font-mono text-[11px]">{r.reference}</Td>
                  <Td>
                    <div className="text-foreground">{r.donor_name}</div>
                    <div className="text-[10.5px] text-muted-foreground">{r.donor_code}</div>
                  </Td>
                  <Td className="font-mono">{r.currency} {r.amount.toLocaleString()}</Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      {r.missing_labels.map((m) => (
                        <Chip key={m} className="border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                          {m}
                        </Chip>
                      ))}
                    </div>
                  </Td>
                  <Td>
                    <div className="flex flex-wrap items-center justify-end gap-1.5">
                      {mayBuild && <AskDonorButton row={r} onDone={load} />}
                      {mayBuild && (
                      <Dialog open={fillOpen === r.donation_id}
                              onOpenChange={(o) => {
                                setFillOpen(o ? r.donation_id : "");
                                if (o) {
                                  setFill({
                                    title: r.title || "", first_name: r.first_name || "",
                                    last_name: r.last_name || "",
                                    address_line: r.address_line || "",
                                    postcode: r.postcode || "", tax_id: r.tax_id || "",
                                  });
                                }
                              }}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline"
                                  data-testid={`claim-fill-${r.reference}`}
                                  className="h-7 rounded-full border-border px-2.5 text-[10px]">
                            Fill in details
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle>{r.donor_name} — claim details</DialogTitle>
                          </DialogHeader>
                          <div className="grid grid-cols-2 gap-3">
                            {[["title", "Title"], ["first_name", "First name"],
                              ["last_name", "Last name"], ["postcode", "Postcode"],
                              ["tax_id", "PAN / tax reference"]].map(([key, label]) => (
                                <div key={key} className="space-y-1.5">
                                  <Label className="text-[11px] text-muted-foreground">{label}</Label>
                                  <Input value={fill[key] || ""} data-testid={`claim-field-${key}`}
                                         onChange={(e) => setFill({ ...fill, [key]: e.target.value })}
                                         className="bg-muted/40 text-[12px]" />
                                </div>
                            ))}
                            <div className="col-span-2 space-y-1.5">
                              <Label className="text-[11px] text-muted-foreground">
                                Address — house name or number
                              </Label>
                              <Input value={fill.address_line || ""}
                                     data-testid="claim-field-address_line"
                                     onChange={(e) => setFill({ ...fill, address_line: e.target.value })}
                                     className="bg-muted/40 text-[12px]" />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button onClick={() => saveDetails(r)} disabled={busy === r.donation_id}
                                    data-testid="claim-fill-save"
                                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                              {busy === r.donation_id
                                && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                              Save details
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </Table>
          </Panel>
        </TabsContent>

        <TabsContent value="register" className="mt-4">
          <Panel testId="panel-claim-register" kicker="Every claim built"
                 title="Claim register">
            <Table testId="claim-register-table"
                   head={<><Th>Reference</Th><Th>Regime</Th><Th>Period</Th><Th>Gifts</Th>
                     <Th>Total</Th><Th>Reclaim</Th><Th>Status</Th><Th /></>}>
              {(claims?.items || []).length === 0 && (
                <Empty colSpan={8} label="No claims have been built yet" />
              )}
              {(claims?.items || []).map((c) => (
                <tr key={c.claim_ref} className="row-hover cursor-pointer"
                    data-testid={`claim-register-${c.claim_ref}`}
                    onClick={rowClick(() => detail.show(c, c.claim_ref, c.claim_ref,
                                                        c.regime_label))}>
                  <Td className="font-mono text-[11px]">{c.claim_ref}</Td>
                  <Td className="text-[11.5px]">{c.regime_label}</Td>
                  <Td className="font-mono text-[10.5px]">
                    {shortDate(c.period_from)} → {shortDate(c.period_to)}
                  </Td>
                  <Td>{c.gift_count}</Td>
                  <Td className="font-mono">{c.currency} {c.total.toLocaleString()}</Td>
                  <Td className="font-mono">{c.reclaim ? c.reclaim.toLocaleString() : "—"}</Td>
                  <Td>
                    <Chip data-testid={`claim-status-${c.claim_ref}`}
                          className={STATUS_TONE[c.status] || "border-border bg-muted text-muted-foreground"}>
                      {(c.status || "").replace("_", " ")}
                    </Chip>
                    {c.rejected_count ? (
                      <div className="mt-1 text-[10px] text-amber-600 dark:text-amber-400">
                        {c.rejected_count} knocked back
                      </div>
                    ) : null}
                    {(c.posting || {}).approval_ref && !(c.posting || {}).posted ? (
                      <div className="mt-1 text-[10px] text-muted-foreground">
                        posting awaits {c.posting.approval_ref}
                      </div>
                    ) : null}
                  </Td>
                  <Td>
                    <ClaimActions claim={c} mayWrite={mayBuild} mayReopen={mayReopen}
                                  onDone={load} onDownload={download} onPrint={printSummary} />
                  </Td>
                </tr>
              ))}
            </Table>
            {claims?.items?.length ? (
              <div className="border-t border-border px-4 py-2.5 text-[11px] text-muted-foreground">
                {claims.counters.gifts} gifts claimed across {claims.items.length} claims ·
                last built {dateTime(claims.items[0].created_at)}
              </div>
            ) : null}
          </Panel>
        </TabsContent>
      </Tabs>

      {detail.sheet}
    </div>
  );
}
